import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import type { ColDef, ColGroupDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { createSearchParams, useNavigate } from 'react-router';

import { noDataRenderer } from '@/components/charts/charts.utils';
import DonutChart, {
  type DonutChartDataTypes
} from '@/components/charts/DonutChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import StackedBarChart from '@/components/charts/StackedBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import PercentageBarChart from '@/components/PercentageBarWidget/PercentageBarWidget';
import { DATE_FORMAT } from '@/constants/date.constants';
import { useGridTheme } from '@/hooks/useGridTheme';
import { selectedOrganizationAtom } from '@/pages/Organizations/Organizations.store';
import { TICKET_THREAT_RESOLUTIONS } from '@/pages/Ticket';
import { userIsInternalAtom } from '@/store/user.store';

import { dashboardTimeFilterAtom } from '../Dashboard.store';
import { createTicketColumns } from '../Dashboard.utils';
import useDashboardV2Tickets from '../DashboardV2Tickets.hooks';
import {
  STACKED_BAR_SERIES,
  TICKET_CATEGORIES_BAR_SERIES,
  TICKET_RESOLUTION_BAR_SERIES
} from './DashboardTickets.constants';
import useStyles from './DashboardTickets.styles';
import { TicketsSmallCards } from './TicketsSmallCards';

const DashboardTickets = () => {
  const {
    threatResolutionBarData,
    threatCategoriesBarData,
    serviceCategoriesBarData,
    threatStatusDonutData,
    serviceStatusDonutData,
    threatPercentageBarData,
    topServiceTickets,
    topThreatTickets,
    highPriorityThreatTicketsCount,
    unresolvedThreatTicketsCount,
    openThreatTicketsCount,
    threatTicketsByDate,
    isFetchingServiceGroupedData,
    isFetchingThreatGroupedData,
    isFetchingTopThreatTickets,
    isFetchingTopServiceTickets,
    isFetchingThreatTicketsByDate
  } = useDashboardV2Tickets();

  const userIsInternal = useAtomValue(userIsInternalAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const dashboardTimeFilter = useAtomValue(dashboardTimeFilterAtom);

  const { classes } = useStyles({ userIsInternal });
  const gridTheme = useGridTheme();
  const theme = useTheme();

  const navigate = useNavigate();

  // When bar area of chart clicked, navigate to tickets page with category filter
  const handleChartClick = useCallback(
    (value: string | number, type: number, filter: string, date?: string) => {
      if (filter === 'ticket_priority__name') {
        const nextDay = dayjs(date).add(1, 'day').format(DATE_FORMAT);
        navigate({
          pathname: '/tickets',
          search: createSearchParams({
            t: `custom-${date},${nextDay}|C`,
            f: JSON.stringify({
              ticket_priority__name: ['__in', value],
              ticket_type: ['__eq', type]
            })
          }).toString()
        });
      } else if (filter === 'ticket_status__name') {
        navigate({
          pathname: '/tickets',
          search: createSearchParams({
            t: dashboardTimeFilter,
            f: JSON.stringify({
              ticket_status__name: ['__in', value],
              ticket_type: ['__eq', type]
            })
          }).toString()
        });
      } else {
        navigate({
          pathname: '/tickets',
          search: createSearchParams({
            t: dashboardTimeFilter,
            f: JSON.stringify({
              [filter]: ['__in', value],
              ticket_type: ['__eq', type]
            })
          }).toString()
        });
      }
    },
    [navigate, dashboardTimeFilter]
  );

  // When bar clicked, navigate to tickets page with priority filter
  const handleBarClick = useCallback(
    (value: string, _data?: string) => {
      navigate(
        `/tickets?t=${dashboardTimeFilter}&f={"ticket_priority__name":["__in","${value}"], "ticket_type":["__eq",1]}`
      );
    },
    [dashboardTimeFilter]
  );

  const dashboardGridTheme = useMemo(
    () =>
      gridTheme.withParams({
        backgroundColor: theme.vars.palette.background.elevation2
      }),
    [gridTheme, theme]
  );

  const onRowClick = useCallback(
    (event: RowClickedEvent) => {
      navigate(`/ticket/${event.data.id}`);
    },
    [navigate]
  );

  return (
    <div className={classes.dashboardPageContainer}>
      <Typography variant="h4">Threat Tickets</Typography>
      <Divider sx={{ mb: 2.5 }} />
      <div className={classes.dashboardThreatTickets}>
        <TicketsSmallCards
          classes={classes}
          selectedOrganization={selectedOrganization}
          threatAwaitingRemTicketsCount={unresolvedThreatTicketsCount}
          threatHighPriorityTicketsCount={highPriorityThreatTicketsCount}
          threatOpenTicketsCount={openThreatTicketsCount}
          timeFilter={dashboardTimeFilter}
        />
        <div className={classes.dashboardXLargeItem}>
          <GenericChartWidget
            title="Threat Tickets Per Day"
            loading={isFetchingThreatTicketsByDate}
          >
            <StackedBarChart
              handleChartClick={(value: string, date?: Date) =>
                handleChartClick(
                  value,
                  1,
                  'ticket_priority__name',
                  dayjs(date).format(DATE_FORMAT)
                )
              }
              data={threatTicketsByDate}
              series={STACKED_BAR_SERIES as AgBarSeriesOptions[]}
            />
          </GenericChartWidget>
        </div>
        {userIsInternal && (
          <div className={classes.dashboardMediumItem}>
            <GenericChartWidget
              title="Top Threat Ticket Resolutions"
              loading={isFetchingTopThreatTickets}
            >
              <HorizontalBarChart
                data={threatResolutionBarData}
                series={TICKET_RESOLUTION_BAR_SERIES}
                handleChartClick={(value: string) => {
                  const resolution = TICKET_THREAT_RESOLUTIONS.find(
                    (res) => res.text === value
                  );
                  handleChartClick(resolution?.id || 0, 1, 'resolution_id');
                }}
              />
            </GenericChartWidget>
          </div>
        )}
        <div className={classes.dashboardMediumItem}>
          <GenericChartWidget
            title="Threat Tickets by Priority"
            loading={isFetchingThreatGroupedData}
          >
            <PercentageBarChart
              handleBarClick={handleBarClick}
              data={threatPercentageBarData as PercentageBarDataType[]}
            />
          </GenericChartWidget>
        </div>
        <div className={classes.dashboardLargeItem}>
          <GenericChartWidget
            title="Top Threat Ticket Categories"
            loading={isFetchingThreatGroupedData}
          >
            <HorizontalBarChart
              data={threatCategoriesBarData}
              series={TICKET_CATEGORIES_BAR_SERIES}
              handleChartClick={(value: string) =>
                handleChartClick(value, 1, 'ticket_category__name')
              }
            />
          </GenericChartWidget>
        </div>
        <div className={classes.dashboardLargeItem}>
          <GenericChartWidget
            title="Threat Tickets by Status"
            loading={isFetchingThreatGroupedData}
          >
            <DonutChart
              data={threatStatusDonutData as DonutChartDataTypes[]}
              legendPosition="left"
              resolveCssVariables
              onChartClick={(event) => {
                handleChartClick(event.datum.name, 1, 'ticket_status__name');
              }}
            />
          </GenericChartWidget>
        </div>
        <div className={classes.dashboardLargeItem}>
          <AgGridReact
            rowData={topThreatTickets || []}
            columnDefs={
              createTicketColumns('Recent Threat Tickets') as (
                | ColDef
                | ColGroupDef
              )[]
            }
            theme={dashboardGridTheme}
            rowHeight={50}
            className={classes.ticketsTable}
            overlayNoRowsTemplate={noDataRenderer()}
            onRowClicked={onRowClick}
          />
        </div>
      </div>
      <Typography variant="h4" sx={{ pt: 3 }}>
        Service Tickets
      </Typography>
      <Divider sx={{ mb: 2.5 }} />
      <div className={classes.dashboardServiceTickets}>
        <div className={classes.dashboardLargeItem}>
          <GenericChartWidget
            title="Top Service Ticket Categories"
            loading={isFetchingTopServiceTickets}
          >
            <HorizontalBarChart
              data={serviceCategoriesBarData}
              series={TICKET_CATEGORIES_BAR_SERIES}
              handleChartClick={(value: string) =>
                handleChartClick(value, 2, 'ticket_category__name')
              }
            />
          </GenericChartWidget>
        </div>
        <div className={classes.dashboardLargeItem}>
          <GenericChartWidget
            title="Service Tickets by Status"
            loading={isFetchingServiceGroupedData}
          >
            <DonutChart
              data={serviceStatusDonutData as DonutChartDataTypes[]}
              legendPosition="left"
              resolveCssVariables
              onChartClick={(event) => {
                handleChartClick(event.datum.name, 2, 'ticket_status__name');
              }}
            />
          </GenericChartWidget>
        </div>
        <div className={classes.dashboardLargeItem}>
          <AgGridReact
            rowData={topServiceTickets || []}
            columnDefs={
              createTicketColumns('Recent Service Tickets') as (
                | ColDef
                | ColGroupDef
              )[]
            }
            theme={dashboardGridTheme}
            rowHeight={50}
            className={classes.ticketsTable}
            overlayNoRowsTemplate={noDataRenderer()}
            onRowClicked={onRowClick}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardTickets;
